import React, { useEffect, useState } from 'react';
import Dialog from 'Components/DesignSystem/Dialog/Dialog';
import Dropdown from 'Components/DesignSystem/Dropdown/Dropdown';
import Button from 'Components/DesignSystem/Button/Button';
import Typography from 'Components/DesignSystem/Typography/Typography';
import FieldTitle from 'Components/DesignSystem/FieldTitle/FieldTitle';
import Radio from 'Components/DesignSystem/Radio/Radio';
import Alert from 'Components/DesignSystem/Alert/Alert';
import TextField from 'Components/DesignSystem/TextField/TextField';
import { TISRProject, TEnvironmentOption, TPushToEnvironmentPayload } from './DefineModel/model';
import { getModelsForEnvironment } from './DefineModel/ModelServices/ModalServices';

type PushToEnvironmentDialogProps = {
    isOpen: boolean;
    onClose: () => void;
    item: TISRProject;
    environmentOptions: TEnvironmentOption[];
    industryId: string;
    onSubmit: (payload: TPushToEnvironmentPayload) => Promise<{ done: boolean; message?: string }>;
};

type TAction = 'override' | 'create';

const PushToEnvironmentDialog = ({ isOpen, onClose, item, environmentOptions, industryId, onSubmit }: PushToEnvironmentDialogProps) => {
    const [selectedEnvironment, setSelectedEnvironment] = useState<string>('');

    const [selectedAction, setSelectedAction] = useState<TAction>('override');
    const [targetModelOptions, setTargetModelOptions] = useState<TEnvironmentOption[]>([]);
    const [targetModelId, setTargetModelId] = useState<string>('');
    const [isLoadingModels, setIsLoadingModels] = useState<boolean>(false);

    const [newModelName, setNewModelName] = useState<string>('');
    const [nameError, setNameError] = useState<string>('');

    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const [showSuccessDialog, setShowSuccessDialog] = useState<boolean>(false);

    useEffect(() => {
        if (!selectedEnvironment) return;
        const fetchModels = async () => {
            setIsLoadingModels(true);
            setTargetModelId('');
            const models = await getModelsForEnvironment(selectedEnvironment, industryId);
            setTargetModelOptions(models);
            setIsLoadingModels(false);
        };
        fetchModels();
    }, [selectedEnvironment]);

    const handleEnvironmentChange = (val: string | number) => {
        setSelectedEnvironment(String(val));
        setSelectedAction('override');
        setTargetModelId('');
        setNewModelName('');
        setNameError('');
    };

    const handleActionChange = (val: string | number) => {
        setSelectedAction(String(val) as TAction);
        setTargetModelId('');
        setNewModelName('');
        setNameError('');
    };

    const validateName = () => {
        if (selectedAction === 'create' && !newModelName.trim()) {
            setNameError('Model name is required.');
        }
    };

    const isSubmitDisabled =
        !selectedEnvironment ||
        isSubmitting ||
        (selectedAction === 'override' && !targetModelId) ||
        (selectedAction === 'create' && (!newModelName.trim() || !!nameError));

    const handleSubmit = async () => {
        if (isSubmitDisabled) return;
        const payload: TPushToEnvironmentPayload = {
            source_model_id: item.id,
            target_env_key: selectedEnvironment,
            action: selectedAction,
            ...(selectedAction === 'override' ? { target_model_id: targetModelId } : { new_model_name: newModelName.trim() }),
        };
        setIsSubmitting(true);
        const result = await onSubmit(payload);
        setIsSubmitting(false);
        if (result.done) {
            setShowSuccessDialog(true);
        } else if (result.message?.toLowerCase().includes('name')) {
            setNameError(result.message ?? 'This name already exists in the target environment. Please choose a different name.');
        }
    };

    const handleClose = () => {
        setSelectedEnvironment('');
        setSelectedAction('override');
        setTargetModelOptions([]);
        setTargetModelId('');
        setNewModelName('');
        setNameError('');
        setShowSuccessDialog(false);
        onClose();
    };

    if (showSuccessDialog) {
        return (
            <Dialog
                isOpen
                onClose={handleClose}
                title="Push to Environment"
                size="md"
                className="!tw-w-[544px] !tw-max-w-[544px]"
                footer={
                    <div className="tw-flex tw-justify-end">
                        <Button variant="primary" onClick={handleClose}>Close</Button>
                    </div>
                }
            >
                <Typography variant="body-primary" className="tw-text-ink">
                    {selectedAction === 'override'
                        ? 'Model successfully overridden and pushed to the selected environment.'
                        : 'New model successfully created and pushed to the selected environment.'}
                </Typography>
            </Dialog>
        );
    }

    return (
        <Dialog
            isOpen={isOpen}
            onClose={handleClose}
            title="Push to Environment"
            size="md"
            className="!tw-w-[544px] !tw-max-w-[544px]"
            footer={
                <div className="tw-flex tw-justify-end">
                    <Button
                        variant="primary"
                        disabled={isSubmitDisabled}
                        onClick={handleSubmit}
                    >
                        Push to Environment
                    </Button>
                </div>
            }
        >
            <div className="tw-flex tw-flex-col tw-gap-5">

                {/* DS `Alert`, not a hand-rolled box. It shipped as a div wearing a tint, a
                    border and an `InfoIcon`, which is precisely what this component is — and
                    hand-rolling it is why it did not theme: the tint/ink pair was picked for
                    light and the border was the saturated GLYPH role, which reads as a harsh
                    outline on a dark ground. `Alert` owns the tone triple and the mark. */}
                <Alert tone="warning" data-testid="push-env-promote-notice">
                    After pushing, the model must be promoted in the target environment to reflect changes on the live site.
                </Alert>

                {/* Model name (read-only) */}
                <div className="tw-flex tw-flex-col tw-gap-1">
                    <FieldTitle label="Model Name" size="sm" />
                    <Typography variant="title-header" className="tw-text-ink">
                        {item?.ISRName}
                    </Typography>
                </div>

                {/* Target environment dropdown */}
                <Dropdown
                    label="Target Environment"
                    options={environmentOptions}
                    value={selectedEnvironment}
                    onChange={handleEnvironmentChange}
                    placeholder="Select"
                    usePortal
                />

                {/* Blue info note — only meaningful when overriding: a new model gets a new ID.
                    Unmounted (not hidden) for Create New Model so the flex gap closes up. */}
                {selectedEnvironment && selectedAction === 'override' && (
                    <Alert tone="info" data-testid="push-env-id-notice">
                        The model ID in the target environment will not change. Only the model contents will be updated.
                    </Alert>
                )}

                {/* Select Action — shown after environment is selected */}
                {selectedEnvironment && (
                    <div className="tw-flex tw-flex-col tw-gap-3">
                        <FieldTitle label="Select Action" size="sm" />

                        <div className="tw-flex tw-flex-col tw-gap-2">
                            <Radio
                                name="push-action"
                                label="Override Existing Model"
                                value="override"
                                checked={selectedAction === 'override'}
                                onChange={handleActionChange}
                            />
                            <Radio
                                name="push-action"
                                label="Create New Model"
                                value="create"
                                checked={selectedAction === 'create'}
                                onChange={handleActionChange}
                            />
                        </div>

                        {/* Override: target model dropdown */}
                        {selectedAction === 'override' && (
                            <Dropdown
                                label="Target Model to Override"
                                options={targetModelOptions}
                                value={targetModelId}
                                onChange={(val) => setTargetModelId(String(val))}

                                placeholder={isLoadingModels ? 'Loading...' : 'Select'}
                                disabled={isLoadingModels}
                                usePortal
                            />
                        )}

                        {/* Create: new model name input */}
                        {selectedAction === 'create' && (
                            <TextField
                                label="New Model Name"
                                placeholder="Enter Name"
                                value={newModelName}
                                onChange={(val) => { setNewModelName(val); setNameError(''); }}
                                onBlur={validateName}
                                error={!!nameError}
                                helperText={nameError}
                            />
                        )}
                    </div>
                )}
            </div>
        </Dialog>
    );
};

export default PushToEnvironmentDialog;
